import React from 'react';
import { Modal as BootstrapModal } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import styles from './Modal.module.scss';

/**
 * 🎨 Modal Padrão - Componente de Modal Moderno
 * 
 * Features:
 * - Design moderno com lightbox backdrop
 * - Suporte a dark/light mode automático
 * - Header customizável com ícone
 * - Footer flexível
 * - Scrollbar customizado
 * - Animações suaves
 * 
 * @example
 * <Modal
 *   show={showModal}
 *   onHide={handleClose}
 *   title="Título do Modal"
 *   icon="mdi:cash"
 *   size="lg"
 *   footer={
 *     <>
 *       <Modal.Button variant="secondary" onClick={handleClose}>
 *         Cancelar
 *       </Modal.Button>
 *       <Modal.Button variant="primary" onClick={handleSubmit}>
 *         Confirmar
 *       </Modal.Button>
 *     </>
 *   }
 * >
 *   Conteúdo do modal aqui
 * </Modal>
 */

const Modal = ({ 
  show, 
  onHide, 
  title,
  icon,
  children,
  footer,
  size = 'md',
  centered = true,
  closeButton = true,
  className = ''
}) => {
  return (
    <BootstrapModal 
      show={show} 
      onHide={onHide}
      size={size}
      centered={centered}
      backdrop="static"
      backdropClassName="modal-backdrop-lightbox"
      className={`${styles.modernModal} ${className}`}
    >
      <BootstrapModal.Header closeButton={closeButton} className={styles.modalHeader}>
        <BootstrapModal.Title className={styles.modalTitle}>
          {icon && <Icon icon={icon} className={styles.titleIcon} />}
          {title}
        </BootstrapModal.Title>
      </BootstrapModal.Header>

      <BootstrapModal.Body className={styles.modalBody}>
        {children}
      </BootstrapModal.Body>

      {footer && (
        <BootstrapModal.Footer className={styles.modalFooter}>
          {footer}
        </BootstrapModal.Footer>
      )}
    </BootstrapModal>
  );
};

// ========================================
// SUB-COMPONENTES DE BOTÃO
// ========================================

/**
 * Botão do Modal
 * 
 * @param {string} variant - 'primary', 'secondary', 'success', 'danger'
 * @param {boolean} loading - Mostra estado de loading
 * @param {boolean} fullWidth - Ocupa largura total
 */
Modal.Button = ({ 
  children, 
  onClick, 
  variant = 'primary', 
  loading = false,
  disabled = false,
  fullWidth = false,
  icon,
  className = ''
}) => {
  const buttonClass = `${styles.modalButton} ${styles[variant]} ${fullWidth ? styles.fullWidth : ''} ${className}`;
  
  return (
    <button
      type="button"
      className={buttonClass}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading ? (
        <>
          <Icon icon="mdi:loading" className={styles.spin} />
          Processando...
        </>
      ) : (
        <>
          {icon && <Icon icon={icon} className={styles.buttonIcon} />}
          {children}
        </>
      )}
    </button>
  );
};

/**
 * Divisor do Modal
 */
Modal.Divider = () => {
  return <div className={styles.divider} />;
};

/**
 * Seção do Modal com título
 */
Modal.Section = ({ title, children, className = '' }) => {
  return (
    <div className={`${styles.section} ${className}`}>
      {title && (
        <span className={styles.sectionTitle}>
          {title}
        </span>
      )}
      {children}
    </div>
  );
};

/**
 * Card informativo dentro do modal
 */
Modal.InfoCard = ({ children, variant = 'info', className = '' }) => {
  return (
    <div className={`${styles.infoCard} ${styles[`infoCard${variant.charAt(0).toUpperCase() + variant.slice(1)}`]} ${className}`}>
      {children}
    </div>
  );
};

/**
 * Estado vazio
 */
Modal.EmptyState = ({ icon, message, children }) => {
  return (
    <div className={styles.emptyState}>
      {icon && <Icon icon={icon} className={styles.emptyIcon} />}
      {message && <p className={styles.emptyText}>{message}</p>}
      {children}
    </div>
  );
};

export default Modal;




